import axios from 'axios';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { useQuery } from '@tanstack/react-query';

// Every component used to fire its own `?filters[name][$contains]=...` request,
// which meant 7-11 round trips for the translations alone on a cold page load.
// The whole `translations` collection is small (~25 KB gzip per locale), so it
// is fetched once per locale and every component picks its entries out of it.
const BUNDLED_ENDPOINT = 'translations';
const BUNDLE_PAGE_SIZE = 500;

const fetchBundle = (locale: string) => {
  const query = qs.stringify(
    { locale, pagination: { pageSize: BUNDLE_PAGE_SIZE } },
    { encodeValuesOnly: true }
  );
  return axios.get(`/${BUNDLED_ENDPOINT}?${query}`).then((r) => r.data.data);
};

const fetchFiltered = (
  endpoint: string,
  name: string | Array<string>,
  locale: string
) => {
  const query = qs.stringify(
    { filters: { name: { $contains: name } }, locale },
    { encodeValuesOnly: true }
  );
  return axios.get(`/${endpoint}?${query}`).then((r) => r.data.data);
};

// Reproduces what the API did for us: `$contains` is a case-sensitive substring
// match, several names are OR-ed, and the caller gets either the sorted list
// (more than one hit) or the payload of the single hit.
const selectTranslation = (
  entries: Array<any>,
  name: string | Array<string>
) => {
  const names = Array.isArray(name) ? name : [name];
  const matched = entries.filter((entry: any) =>
    names.some((n) => entry?.name?.includes(n))
  );
  if (matched.length === 0) {
    return {};
  }
  return matched.length > 1
    ? [...matched].sort((a: any, b: any) => a.name.localeCompare(b.name))
    : matched[0].data;
};

const withTranslation =
  (
    name: string | Array<string>,
    endpoint: string = BUNDLED_ENDPOINT,
    getCustomLocale: Function = () => null
  ) =>
  (WrappedComponent: any) => {
    return (props: any) => {
      const [loaded, setLoaded] = useState(false);
      const { i18n } = useTranslation();
      const customLocale =
        typeof getCustomLocale === 'function' ? getCustomLocale(props) : null;

      const localeShortcut = customLocale || i18n?.language?.slice(0, 2);
      const isBundled = endpoint === BUNDLED_ENDPOINT;

      const { data } = useQuery({
        queryKey: isBundled
          ? ['translation-bundle', localeShortcut]
          : ['translation', endpoint, localeShortcut, JSON.stringify(name)],
        queryFn: () =>
          isBundled
            ? fetchBundle(localeShortcut)
            : fetchFiltered(endpoint, name, localeShortcut),
        enabled: !!localeShortcut,
      });

      const translation = useMemo(
        () => (Array.isArray(data) ? selectTranslation(data, name) : {}),
        [data]
      );

      useEffect(() => {
        if (Object.keys(translation).length > 0) {
          setLoaded(true);
        }
      }, [translation]);

      return (
        <WrappedComponent
          translation={translation}
          translationLoaded={loaded}
          {...props}
        />
      );
    };
  };
export default withTranslation;
